import React, { useEffect, useState, useRef } from "react";
import { View } from "react-native";
import { useRouter, useGlobalSearchParams } from "expo-router";
import { ClientType } from "@/types/db/clients";
import {
  InvoiceCreateForm,
  InvoiceCreateFormRef,
  InvoicePayload,
  InvoiceForm,
  INVOICE_STATUS_OPTIONS,
  getInvoiceStatusColor,
} from "@/components/screens/invoice/NewInvoiceForm";
import { InvoiceStatusType } from "@/types/db/invoices";
import { useTheme } from "@/hooks/useTheme";
import { CreateHeader } from "@/components/screens/shared";
import useToast from "@/hooks/useToast";
import { invoiceService } from "@/services/invoiceService";
import { billingItemService } from "@/services/billingItemService";
import { useRowSelectionContext } from "@/providers/RowSelectionProvider";
import { supabase } from "@/lib/supabase-client";
import useAuth from "@/hooks/useAuth";
import Head from "expo-router/head";
import { getSaveBehavior } from "@/lib/saveSettings";

export default function NewInvoicePage() {
  const toast = useToast();
  const router = useRouter();
  const { colors } = useTheme();
  const params = useGlobalSearchParams<{ billing_items?: string; new?: string }>();
  const { tenantId } = useAuth();
  const rowSelection = useRowSelectionContext();
  const [localDraft, setLocalDraft] = useState<InvoicePayload>();
  const [dirty, setDirty] = useState(false);
  const [sidebarClient, setSidebarClient] = useState<ClientType | null>(null);
  const [invoiceName, setInvoiceName] = useState("");
  const [status, setStatus] = useState<InvoiceStatusType>("DRAFT");
  const [initialInvoice, setInitialInvoice] = useState<Partial<InvoiceForm>>({});
  const [fieldErrors, setFieldErrors] = useState<{ client_id?: string; project_id?: string }>({});
  const detectedScalars = useRef<{ client_id?: string; project_id?: string }>({});
  const formRef = useRef<InvoiceCreateFormRef>(null);

  // Fetch next invoice number on mount
  useEffect(() => {
    const fetchNextNumber = async () => {
      try {
        const nextNumber = await invoiceService.getNextInvoiceNumber();
        setInvoiceName(nextNumber);
        formRef.current?.updateInvoiceName(nextNumber);
      } catch (error) {
        console.error("Failed to fetch next invoice number:", error);
      }
    };
    fetchNextNumber();
  }, []);

  // Convert selected billing items to line items when billing_items param is present
  // Reads row data directly from RowSelectionProvider instead of re-fetching from DB
  useEffect(() => {
    const selectedBillingItems = rowSelection.getEntitySelection("billing_item");
    const items = Object.values(selectedBillingItems);
    if (items.length === 0) return;

    const resolveAndConvert = async () => {
      try {
        // Collect unique shift IDs and project IDs (project_id from billing item data)
        const shiftIds = [...new Set(items.map((i: any) => i.source?.shift_id).filter(Boolean))] as string[];
        const projectIds = [...new Set(items.map((i: any) => i.source?.project_id).filter(Boolean))] as string[];

        // Build shift→project map from billing item data (shift.project_id may be null in DB)
        const shiftToProjectMap = new Map<string, string>();
        items.forEach((i: any) => {
          const sid = i.source?.shift_id;
          const pid = i.source?.project_id;
          if (sid && pid) shiftToProjectMap.set(sid, pid);
        });

        // Fetch shift start times and location_ids
        let shiftStartMap = new Map<string, string>();
        let shiftLocationMap = new Map<string, string>();
        const shiftLocIdMap = new Map<string, string | null>();

        if (shiftIds.length > 0) {
          const { data: shifts } = await supabase
            .from("shifts")
            .select("id, start_time, location_id")
            .in("id", shiftIds);

          if (shifts) {
            shifts.forEach((s: any) => {
              shiftStartMap.set(s.id, s.start_time);
              shiftLocIdMap.set(s.id, s.location_id || null);
            });
          }
        }

        // NOTE: location resolution below is kept but currently UNUSED — the shift
        // label no longer appends the location (see shift_name above). Left intact
        // so it can be re-enabled without re-deriving the shift→location fallback.
        // Fetch project location_ids for fallback (inherited location)
        const projectLocIdMap = new Map<string, string | null>();
        if (projectIds.length > 0) {
          const { data: projects } = await supabase.from("projects").select("id, location_id").in("id", projectIds);

          if (projects) {
            projects.forEach((p: any) => {
              projectLocIdMap.set(p.id, p.location_id || null);
            });
          }
        }

        // Resolve effective location_id per shift: shift's own location, or project's location
        const locationIds = new Set<string>();
        shiftIds.forEach((sid) => {
          const locId = shiftLocIdMap.get(sid) || projectLocIdMap.get(shiftToProjectMap.get(sid) || "");
          if (locId) locationIds.add(locId);
        });

        // Fetch location names
        if (locationIds.size > 0) {
          const { data: locations } = await supabase
            .schema("lists")
            .from("locations")
            .select("id, name")
            .in("id", Array.from(locationIds));

          const locMap = new Map(locations?.map((l) => [l.id, l.name]) || []);
          shiftIds.forEach((sid) => {
            const locId = shiftLocIdMap.get(sid) || projectLocIdMap.get(shiftToProjectMap.get(sid) || "");
            if (locId && locMap.has(locId)) {
              shiftLocationMap.set(sid, locMap.get(locId)!);
            }
          });
        }

        // Convert billing items to line items (matching LineItem schema)
        // Row selection data uses time_card for meta_data
        // Uses effective values: override ?? original for rate, quantity, amount
        const lineItems = items.map((item: any) => {
          const md = item.source || {};

          // Effective rate string (override takes precedence)
          const effectiveRateStr: string = md.rate_override || md.rate || "";
          // Parse numeric rate from string like "$35.50/hr"
          const rateMatch = effectiveRateStr.match(/\$?([\d.]+)/);
          const effectiveRate = rateMatch ? parseFloat(rateMatch[1]) : 0;

          // Effective quantity (override takes precedence)
          const effectiveQty = md.quantity_override != null ? parseFloat(md.quantity_override) : md.quantity || 1;

          // Admin fee and discount percentages from billing item meta_data
          const adminFee = parseFloat(md.admin_fee) || 0;
          const discount = parseFloat(md.discount) || 0;

          // Effective amount = rate * quantity, adjusted for admin fee and discount
          // Round to cents after each step to avoid floating-point drift
          const round2 = (n: number) => Math.round(n * 100) / 100;
          const baseAmount = round2(effectiveRate * effectiveQty);
          const effectiveAmount = round2(
            baseAmount + round2((baseAmount * adminFee) / 100) - round2((baseAmount * discount) / 100),
          );

          const isExpense = (md.links?.expenses?.length || 0) > 0;

          // Build description based on source type
          let description = "";
          if (isExpense) {
            description = md.description || "Expense";
          } else {
            // Build worker label: "Rigger John D." (position + name)
            const worker = md.worker;
            // NOTE: worker id prefix removed — show the position instead. Re-enable the id with:
            // `${worker.id?.slice(0, 6).toUpperCase() || ""} ${worker.first_name || ""} ${worker.last_name?.charAt(0) || ""}.`
            const workerLabel = worker
              ? `${md.position_name || ""} ${worker.first_name || ""} ${worker.last_name?.charAt(0) || ""}.`.trim()
              : "";

            if (workerLabel) {
              description = workerLabel;
            } else {
              description = md.description || "Billing Item";
            }
          }

          const shiftId = md.shift_id || undefined;

          return {
            description,
            unit_price: effectiveRate,
            quantity: effectiveQty,
            amount: effectiveAmount,
            admin_fee: adminFee || undefined,
            discount: discount || undefined,
            // Grouping metadata
            // Shift name comes from the get_billing_items RPC at item.shift.name
            // (a sibling of `source`), NOT md.shift_name (which doesn't exist).
            // NOTE: location temporarily removed from the shift label. Re-enable by restoring:
            // shift_name: [item.shift?.name, shiftLocationMap.get(md.shift_id)].filter(Boolean).join(" - ") || undefined,
            shift_name: item.shift?.name || undefined,
            shift_id: shiftId,
            position_name: md.position_name || undefined,
            shift_start: shiftId ? shiftStartMap.get(shiftId) || undefined : undefined,
            time_in: md.start_time || undefined,
            time_out: md.end_time || undefined,
          };
        });

        // Check if client_id and project_id are scalar (all same) across billing items
        const clientIds = [...new Set(items.map((i: any) => i.client_id).filter(Boolean))];
        const scalarClientId = clientIds.length === 1 ? clientIds[0] : undefined;
        const scalarProjectId = projectIds.length === 1 ? projectIds[0] : undefined;

        // Store detected scalars for live validation when user edits fields
        detectedScalars.current = { client_id: scalarClientId, project_id: scalarProjectId };

        const clientError = clientIds.length > 1 ? "Multiple clients in selection" : undefined;
        const projectError = projectIds.length > 1 ? "Multiple projects in selection" : undefined;
        setFieldErrors({ client_id: clientError, project_id: projectError });

        if (clientError || projectError) {
          toast.showToast({
            title: "Selection mismatch",
            message: [clientError, projectError].filter(Boolean).join(". "),
            toastType: "error",
          });
        }

        setInitialInvoice({
          client_id: scalarClientId,
          project_id: scalarProjectId,
          meta_data: {
            status: "DRAFT",
            line_items: lineItems,
          },
        });
      } catch (error) {
        console.error("Failed to convert billing items:", error);
        toast.showToast({
          title: "Failed to load billing items",
          toastType: "error",
        });
      }
    };
    resolveAndConvert();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params.billing_items]);

  // Live validation: warn if user changes client/project away from billing items source
  useEffect(() => {
    const scalars = detectedScalars.current;
    if (!scalars.client_id && !scalars.project_id) return;

    const currentClient = localDraft?.invoice?.client_id;
    const currentProject = localDraft?.invoice?.project_id;

    const clientError =
      scalars.client_id && currentClient && currentClient !== scalars.client_id
        ? "Client differs from billing items"
        : undefined;
    const projectError =
      scalars.project_id && currentProject && currentProject !== scalars.project_id
        ? "Project differs from billing items"
        : undefined;

    setFieldErrors((prev) => {
      if (prev.client_id === clientError && prev.project_id === projectError) return prev;
      return { client_id: clientError, project_id: projectError };
    });
  }, [localDraft?.invoice?.client_id, localDraft?.invoice?.project_id]);

  const handleCreateInvoice = async (payload: any) => {
    try {
      const invoicePayload = payload.invoice;

      const invoiceData = {
        tenant_id: tenantId,
        client_id: invoicePayload.client_id,
        project_id: invoicePayload.project_id,
        status: invoicePayload.status,
        meta_data: {
          invoice_number: invoicePayload.invoice_number,
          invoice_date: invoicePayload.meta_data?.invoice_date || new Date().toISOString(),
          status: invoicePayload.status,
          terms: invoicePayload.meta_data?.terms,
          remittance_address_id: invoicePayload.meta_data?.remittance_address_id,
          line_items: invoicePayload.meta_data?.line_items || [],
        },
      };

      // Create the invoice first
      const createdInvoice = await invoiceService.addInvoice(invoiceData as any);

      if (!createdInvoice?.id) {
        throw new Error("Failed to get invoice ID from created invoice");
      }

      // Link billing items to the created invoice
      const selectedBillingItems = rowSelection.getEntitySelection("billing_item");
      const billingItemIds = Object.keys(selectedBillingItems);
      if (billingItemIds.length > 0) {
        const patches = billingItemIds.map((id) => ({
          id,
          meta_data: {
            links: {
              invoices: [createdInvoice.id],
            },
          },
        }));
        await billingItemService.saveRowPatches(patches);
      }

      // Upload pending files if any
      const pendingFiles = formRef.current?.getPendingFiles() || [];
      if (pendingFiles.length > 0) {
        toast.showToast({
          title: `Uploading ${pendingFiles.length} file(s)...`,
          toastType: "info",
        });

        let uploadedCount = 0;
        for (const attachmentFile of pendingFiles) {
          if (attachmentFile.file) {
            try {
              await invoiceService.uploadFile(createdInvoice.id, attachmentFile.file, tenantId!);
              uploadedCount++;
            } catch (uploadErr) {
              console.error(`Failed to upload file ${attachmentFile.name}:`, uploadErr);
              // Continue with other files even if one fails
            }
          }
        }

        if (uploadedCount > 0) {
          toast.showToast({
            title: `Invoice created with ${uploadedCount} file(s) uploaded`,
            toastType: "success",
          });
        } else {
          toast.showToast({
            title: "Invoice created (file upload failed)",
            toastType: "warning",
          });
        }
      } else {
        toast.showToast({ title: "Invoice created successfully", toastType: "success" });
      }

      // Handle save behavior setting
      const behavior = await getSaveBehavior("invoice");
      switch (behavior) {
        case "view_created":
          router.setParams({ new: undefined, id: createdInvoice.id });
          break;
        case "create_another":
          handleReset();
          break;
        case "close":
        default:
          router.setParams({ new: undefined, panel: undefined });
          break;
      }
    } catch (err: any) {
      toast.showToast({
        title: "Failed to create invoice",
        message: err.message,
        toastType: "error",
      });
    }
  };

  // Sync invoiceName and status with form state
  useEffect(() => {
    if (localDraft?.invoice?.invoice_number !== undefined && localDraft.invoice.invoice_number !== invoiceName) {
      setInvoiceName(localDraft.invoice.invoice_number);
    }
    if (localDraft?.invoice?.status !== undefined && localDraft.invoice.status !== status) {
      setStatus(localDraft.invoice.status);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [localDraft?.invoice?.invoice_number, localDraft?.invoice?.status]);
  const handleReset = () => {
    setSidebarClient(null);
    setStatus("DRAFT");
    formRef.current?.handleReset();
    // Re-fetch invoice number after reset
    const fetchNextNumber = async () => {
      try {
        const nextNumber = await invoiceService.getNextInvoiceNumber();
        setInvoiceName(nextNumber);
        formRef.current?.updateInvoiceName(nextNumber);
      } catch (error) {
        console.error("Failed to fetch next invoice number:", error);
      }
    };
    fetchNextNumber();
  };

  return (
    <>
      <Head>
        <title>NEXUS New Invoice</title>
        <meta name="description" content="" />
      </Head>
      <View style={{ flex: 1 }}>
        <CreateHeader
          entityType="invoice"
          name={invoiceName}
          nameEditable={false}
          namePlaceholder="Invoice #"
          subtitle={sidebarClient?.name}
          dirty={dirty}
          onSave={async () => {
            await formRef.current?.handleSave();
          }}
          onCancel={handleReset}
          status={status}
          onStatusChange={(value) => {
            setStatus(value as InvoiceStatusType);
            formRef.current?.updateStatus(value);
          }}
          statusOptions={INVOICE_STATUS_OPTIONS.map((opt) => ({
            text: opt.text,
            bgColor: getInvoiceStatusColor(opt.text, colors),
          }))}
        />
        <View style={{ flex: 1, padding: 16 }}>
          <InvoiceCreateForm
            key={`invoice-form-${initialInvoice.meta_data?.line_items?.length || 0}`}
            ref={formRef}
            initialInvoice={{ invoice_number: invoiceName, ...initialInvoice }}
            fieldErrors={fieldErrors}
            onChange={(current) => {
              setLocalDraft(current);
            }}
            onSave={handleCreateInvoice}
            onDirtyChange={setDirty}
            onClientChange={setSidebarClient}
          />
        </View>
      </View>
    </>
  );
}
